import * as Sentry from '@sentry/react-native'
import { isRunningInExpoGo } from 'expo'

import {
  isForeignScriptEvent,
  isInjectedLoggerError,
  isUndefinedLocaleError,
} from './sentryEventFilter'
import { shouldTraceRequest } from './sentryRequestFilter'

export const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !isRunningInExpoGo(),
})

Sentry.init({
  enabled: !__DEV__,
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  debug: false,
  environment: process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT,
  // HET-4020: script de terceiro injetado na página pelo navegador do aluno.
  // O `createReactNativeRewriteFrames` do SDK apaga o `abs_path` e reduz o
  // `filename` ao basename (`chrome-extension://<id>/200.js` vira
  // `app:///200.js`), então o `beforeSend` já não enxerga a origem. O
  // `InboundFilters` é registrado ANTES do rewrite na lista de integrações
  // default do SDK, então aqui a URL original ainda está intacta. Isso também
  // cobre o `inpage.js` da MetaMask, que chega no projeto do mesmo jeito.
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(?:web-)?extension:\/\//i,
  ],
  ignoreErrors: [
    'Empty page!',
    /Error invoking postEvent/,
    /Method not found/,
    /signal is aborted without reason/,
    // Aborto de rede do lado do aluno: troca de aba, app em background, sinal
    // caindo, navegação durante um request. Não é bug do app e respondia por
    // ~70% dos eventos do projeto. Queda de backend é acompanhada no Loki.
    /Failed to fetch/,
    /Load failed/,
    /Network request failed/,
    /NetworkError when attempting to fetch resource/,
    /The Internet connection appears to be offline/,
    // HET-4020: rede de segurança para o mesmo erro injetado, caso a origem não
    // seja uma extensão e escape do `denyUrls`. `M_ID` não existe em lugar
    // nenhum do nosso código nem do bundle web publicado, então a regra não tem
    // como esconder bug nosso.
    /Cannot read properties of undefined \(reading 'M_ID'\)/,
    // Assinatura do scanner de link do Outlook/Defender abrindo a página fora
    // de um browser de verdade. Não existe aluno atrás disso.
    /Object Not Found Matching Id/,
    // Listener de `message` que o navegador da smart TV injeta na página e faz
    // `event.data.split(...)` no payload objeto do player. O filtro de frame
    // (`beforeSend` abaixo) já descarta, mas o asset do player é servido com
    // cache de um ano: a TV que já baixou a versão antiga segue emitindo por
    // muito tempo, e por mensagem é garantido independente da forma da pilha.
    // Não existe `.data.split` em nenhum lugar do nosso código.
    /\.data\.split is not a function/,
  ],
  // Erro de script de terceiro injetado na página não é bug do app. Tem que ser
  // aqui e não em `denyUrls`: o filtro do SDK olha só o primeiro frame com URL
  // e pula `<anonymous>` por igualdade exata, mas o rewrite de frames já
  // transformou o nosso em `app:///<anonymous>`. Preço de descartar no
  // `beforeSend`: o SDK marca a sessão como errored DEPOIS deste hook, então
  // evento descartado aqui não conta pro crash-free rate (o do `ignoreErrors`
  // acima conta, porque aquele filtro roda antes, como event processor).
  //
  // HET-3937 e HET-4006 não passam pelo filtro de frame: o primeiro porque a
  // origem sozinha não basta (o in-app browser hospeda o nosso app), o segundo
  // porque a pilha dele não tem nenhum frame com dono. Cada um traz o seu
  // próprio teste, colado na assinatura.
  beforeSend: (event) =>
    isForeignScriptEvent(event) ||
    isInjectedLoggerError(event) ||
    isUndefinedLocaleError(event)
      ? null
      : event,
  // 94% dos spans do projeto são o nosso `POST /graphql`, e o
  // `shouldCreateSpanForRequest` não alcança isso: ele recebe só a URL, que é a
  // mesma para toda operação GraphQL. Com 9,15 milhões de spans em 30 dias, a
  // taxa é a única alavanca — e a 2% a amostra continua na casa das centenas de
  // milhares por mês, de sobra para p95 e p99. O que se perde é resolução de
  // cauda: incidente restrito a uma escola pequena aparece com menos traces.
  tracesSampleRate: 0.02,
  enableAutoPerformanceTracing: true,
  enableNativeFramesTracking: !isRunningInExpoGo(),
  integrations: [
    navigationIntegration,
    // Mesmo nome da integração default ("ReactNativeTracing"), então esta
    // instância substitui a que o SDK registra sozinho.
    Sentry.reactNativeTracingIntegration({
      shouldCreateSpanForRequest: shouldTraceRequest,
    }),
  ],
})
